import React, { useState } from "react";
import { Badge } from "../../../../../components/ui/common/badge";
import CollapsibleSectionCard from "../../shared/components/CollapsibleSectionCard";
import AddRecordPanel from "../../shared/components/recordList/AddRecordPanel";
import { LEVEL_ICONS } from "../constants/packagingLevelSection.constants";
import { EMPTY_MATERIAL } from "../constants/packagingMaterial.constants";
import {
  ApiPackagingLevelType,
  PackagingLevelViewModel,
  PackagingUnitSnapshot
} from "../types/packagingDetail.types";
import { MaterialFormValues, PackagingTypeOption } from "../types/packagingMaterial.types";
import PackagingMaterialForm from "./PackagingMaterialForm";
import PackagingMaterialRow from "./PackagingMaterialRow";
import PackagingUnitRow from "./PackagingUnitRow";

interface PackagingLevelSectionProps {
  level: PackagingLevelViewModel;
  packagingTypeData: PackagingTypeOption[];
  onAddMaterial: (level: ApiPackagingLevelType, values: MaterialFormValues) => void;
  onUpdateMaterial: (level: ApiPackagingLevelType, materialId: string, values: MaterialFormValues) => void;
  onRemoveMaterial: (level: ApiPackagingLevelType, materialId: string) => void;
  onSaveUnit: (patch: Partial<PackagingUnitSnapshot>) => void;
  disabled?: boolean;
}

const PackagingLevelSection: React.FC<PackagingLevelSectionProps> = ({
  level,
  packagingTypeData,
  onAddMaterial,
  onUpdateMaterial,
  onRemoveMaterial,
  onSaveUnit,
  disabled = false
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftKey, setDraftKey] = useState(0);

  const isTransport = level.key === "transportUnitPackaging";
  const isSingleUnit = level.key === "singleUnitPackaging";

  const unitLabel = (() => {
    switch (level.key) {
      case "singleUnitPackaging":
        return "Unit Size";
      case "salesUnitPackaging":
        return "Number of Base Units";
      case "bulkUnitPackaging":
        return "Number of Packs";
      case "transportUnitPackaging":
        return "Number of Cases";
      default:
        return "";
    }
  })();

  const totalWeightLabel = (() => {
    switch (level.key) {
      case "singleUnitPackaging":
        return "Total Weight of Base unit:";
      case "salesUnitPackaging":
        return "Total Weight of Pack:";
      case "bulkUnitPackaging":
        return "Total Weight of Case:";
      case "transportUnitPackaging":
        return "Total Weight of Pallet:";
      default:
        return "";
    }
  })();

  const handleAddSave = (values: MaterialFormValues) => {
    onAddMaterial(level.apiType, values);
    setDraftKey(k => k + 1);
  };

  const handleEditSave = (materialId: string) => (values: MaterialFormValues) => {
    onUpdateMaterial(level.apiType, materialId, values);
    setEditingId(null);
  };

  const handleDelete = (materialId: string) => {
    onRemoveMaterial(level.apiType, materialId);
    setEditingId(null);
  };

  return (
    <CollapsibleSectionCard
      icon={LEVEL_ICONS[level.key]}
      title={level.label}
      disabled={disabled}
      badge={
        level.materials.length > 0 && (
          <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
            {level.materials.length}
          </Badge>
        )
      }
    >
      <PackagingUnitRow
        level={level}
        unitLabel={unitLabel}
        totalWeightLabel={totalWeightLabel}
        isSingleUnit={isSingleUnit}
        isTransport={isTransport}
        disabled={disabled}
        onSaveUnit={onSaveUnit}
      />

      {level.materials.map((material, idx) => (
        <PackagingMaterialRow
          key={material.id}
          material={material}
          idx={idx}
          packagingTypeData={packagingTypeData}
          isEditing={editingId === material.id}
          onEdit={() => setEditingId(material.id)}
          onSave={handleEditSave(material.id)}
          onCancelEdit={() => setEditingId(null)}
          onDelete={() => handleDelete(material.id)}
          disabled={disabled}
        />
      ))}

      <AddRecordPanel label="Add new material record" hasRecords={level.materials.length > 0}>
        <PackagingMaterialForm
          key={draftKey}
          defaultValues={EMPTY_MATERIAL}
          packagingTypeData={packagingTypeData}
          isDraft
          onSave={handleAddSave}
          disabled={disabled}
        />
      </AddRecordPanel>
    </CollapsibleSectionCard>
  );
};

export default PackagingLevelSection;
